// nodeDragData.js — cross-view node drag payload

export const ORBITAL_NODE_DRAG_MIME = 'application/x-orbital-node';

const clean = (value) => String(value || '').trim();

/** In-memory payload — custom MIME types are unreliable in dragover `types` across browsers. */
let activeNodeDrag = null;

const clearActiveNodeDrag = () => {
  activeNodeDrag = null;
};

const scheduleClearOnDragEnd = () => {
  const clear = () => {
    clearActiveNodeDrag();
    window.removeEventListener('dragend', clear, true);
  };
  window.addEventListener('dragend', clear, true);
};

/**
 * Write a node pointer onto a drag event.
 */
export const writeNodeDragData = (event, node, fallbackBaseKey = '') => {
  const dataTransfer = event?.dataTransfer;
  const nodeKey = clean(node?.key);
  if (!dataTransfer || !nodeKey) return false;

  const payload = {
    version: 1,
    kind: 'node',
    nodeKey,
    baseKey: clean(node?.baseKey || fallbackBaseKey),
    typeKey: clean(node?.type),
  };

  try {
    dataTransfer.setData(ORBITAL_NODE_DRAG_MIME, JSON.stringify(payload));
    // Compatibility fallback for browsers that only expose text/plain in dragover.
    dataTransfer.setData('text/plain', nodeKey);
    dataTransfer.effectAllowed = 'copy';
    activeNodeDrag = payload;
    scheduleClearOnDragEnd();
    return true;
  } catch (_error) {
    clearActiveNodeDrag();
    return false;
  }
};

/**
 * Read a node pointer from a drop/dragover DataTransfer.
 * Returns null unless the dedicated MIME (or active drag session) is present.
 */
export const readNodeDragData = (dataTransfer) => {
  if (activeNodeDrag?.kind === 'node' && activeNodeDrag?.nodeKey) {
    return { ...activeNodeDrag };
  }

  if (!dataTransfer) return null;

  let raw = '';
  try {
    raw = dataTransfer.getData(ORBITAL_NODE_DRAG_MIME);
  } catch (_error) {
    return null;
  }

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const nodeKey = clean(parsed?.nodeKey);
    if (!nodeKey || parsed?.kind !== 'node') return null;
    return {
      version: Number(parsed?.version) || 1,
      kind: 'node',
      nodeKey,
      baseKey: clean(parsed?.baseKey),
      typeKey: clean(parsed?.typeKey),
    };
  } catch (_error) {
    return null;
  }
};

/**
 * Whether a DataTransfer currently carries an Orbital node drag.
 */
export const dataTransferHasNodeDrag = (dataTransfer) => {
  if (activeNodeDrag?.kind === 'node' && activeNodeDrag?.nodeKey) {
    return true;
  }
  if (!dataTransfer) return false;
  const types = Array.from(dataTransfer.types || []);
  return types.includes(ORBITAL_NODE_DRAG_MIME);
};
